// Pack desk: "Note on this order" (per Gabe, 2026-10-30).
//
// Shown INSTEAD of the pack screen when the order opens and its thread holds
// a note a person wrote (see packer-notes.tsx), the same way the DSD notice
// is: the only way on is to scan an item of the order, so the packer has
// read the note before packing. No notes, no notice. The scan is the pack
// scan — the parent counts it on its line when the line still has a unit to
// scan. A DSD order shows its notes on the DSD notice instead.
import { useState } from "react";
import { StickyNote, ArrowLeft, ScanBarcode } from "lucide-react";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { itemForScan } from "./dsd-order-notice";
import { PackerNotesList, type PackerNote } from "./packer-notes";
import type { DispatchOrder, DispatchOrderItem } from "../../types/Dispatch";

type Props = {
    order: DispatchOrder;
    notes: PackerNote[];
    /** An item of the order was scanned (a line with a unit left when there is one). */
    onScanned: (item: DispatchOrderItem) => void;
    /** Leave the order without packing it. */
    onLeave?: () => void;
};

const OrderNotesNotice = ({ order, notes, onScanned, onLeave }: Props) => {
    const { t } = useTranslation();
    const [scanValue, setScanValue] = useState("");
    const [shake, setShake] = useState(false);

    const doSubmit = (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val) return;
        const item = itemForScan(order, val);
        if (!item) {
            playError();
            setShake(true);
            window.setTimeout(() => setShake(false), 400);
            return;
        }
        playSuccess();
        onScanned(item);
    };

    return (
        <div className="dispatch-page page-data fp-notice-page" data-testid="order-notes-notice">
            <div className={`fp-notice dsd-notice${shake ? " dsd-notice--shake" : ""}`}>
                <div className="fp-notice-head">
                    <span className="fp-notice-icon dsd-notice-icon">
                        <StickyNote size={20} />
                    </span>
                    <h2>{t("order_notes_notice_title", { order: order.order_number || order.order_id })}</h2>
                </div>
                <PackerNotesList notes={notes} />
                <p className="dsd-notice-scan">
                    <ScanBarcode size={16} />
                    {t("dsd_notice_scan")}
                </p>
                {onLeave && (
                    <div className="fp-notice-actions">
                        <button type="button" className="btn btn-ghost fp-notice-btn" onClick={onLeave} data-testid="order-notes-notice-leave">
                            <ArrowLeft size={16} />
                            {t("fp_notice_leave")}
                        </button>
                    </div>
                )}
            </div>
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
            />
        </div>
    );
};

export default OrderNotesNotice;
